import Breadcrumb from '../../components/Breadcrumb';
import { getMax5Records } from '../../common/helpers';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import RecentDevices from './components/RecentDevices';
import RecentMerchants from './components/RecentMerchants';
import RecentStores from './components/RecentStores';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useAuth } from '../../app/hooks/useAuth';
import { useGetDevicesQuery } from '../devices/device.api-slice';
import { useGetGlobalDashboardQuery } from './dashboard-api-slice';
import { useGetMerchantsQuery } from '../merchants/merchant.api-slice';
import { useGetStoresQuery } from '../stores/store.api-slice';
import { UserRole } from '../../common/constants';
import { useTranslation } from 'react-i18next';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data: dashboardStat, isLoading } = useGetGlobalDashboardQuery();
  const { data: merchants } = useGetMerchantsQuery(user?.role === UserRole.SUPER_ADMIN ? { page: 1 } : skipToken);
  const { data: stores, isLoading: isLoadingStoresList } = useGetStoresQuery({ page: 1 });
  const { data: devices } = useGetDevicesQuery(user?.role === UserRole.SUPER_ADMIN ? skipToken : { page: 1 });
  const { t } = useTranslation();

  if (isLoading || isLoadingStoresList || user == null) {
    return <LoadingSpinner />;
  }

  const stats = [
    { id: 'merchantCount', title: 'Merchants', count: dashboardStat?.merchantCount },
    { id: 'storeCount', title: 'Stores', count: dashboardStat?.storeCount },
    { id: 'userCount', title: 'Users', count: dashboardStat?.userCount },
    { id: 'deviceCount', title: 'Devices', count: dashboardStat?.deviceCount },
    { id: 'receiptCount', title: 'Receipt Rule', count: dashboardStat?.receiptRuleCount }
  ];
  return (
    <div>
      <div className="pb-4">
        <h1>Dashboard</h1>
        <Breadcrumb
          items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('common.dashboardBreadcrumbLabel') }]}
        />
      </div>
      {/* Stats Card */}
      <div className="w-full flex flex-col md:flex-row justify-between space-y-2 md:space-y-0 space-x-0 md:space-x-2">
        {stats.map((stat) => {
          return (
            <div
              key={stat.id}
              className={`sm:w-full md:w-1/${stats.length} h-24 px-4 py-2 flex flex-col justify-between bg-gray-100 rounded-md`}
            >
              <span className="text-4xl text-center px-2">{stat.count}</span>
              <span className="text-sm text-center">{stat.title}</span>
            </div>
          );
        })}
      </div>

      {/* Recent Lists */}
      <div className="flex flex-col md:flex-row mt-4">
        {user.role === UserRole.SUPER_ADMIN && merchants != null && merchants.data.length > 0 && (
          <div className="w-full md:w-1/2 flex flex-col">
            <div className="w-full flex justify-between px-4">
              <span className="text-lg">Recent Merchants</span>
              <span className="text-white ml-4 px-4 py-1 rounded-xl bg-[#1D8A43]">
                <Link to="/admin/merchants">View All</Link>
              </span>
            </div>
            <RecentMerchants merchants={getMax5Records(merchants.data)} />
          </div>
        )}
        {stores != null && stores.data.length > 0 && (
          <div className="w-full md:w-1/2">
            <div className="w-full flex justify-between px-4 items-center">
              <span className="text-lg">Recent Stores</span>
              <span className="text-white ml-4 px-4 py-1 rounded-xl bg-[#1D8A43]">
                <Link to="/admin/stores">View All</Link>
              </span>
            </div>
            <RecentStores stores={getMax5Records(stores.data)} />
          </div>
        )}
        {user.role !== UserRole.SUPER_ADMIN && devices != null && devices.data.length > 0 && (
          <div className="w-full md:w-1/2 flex flex-col">
            <div className="w-full flex justify-between px-4">
              <span className="text-lg">Recent Devices</span>
              <span className="text-white ml-4 px-4 py-1 rounded-xl bg-[#1D8A43]">
                <Link to="/admin/devices">View All</Link>
              </span>
            </div>
            <RecentDevices devices={getMax5Records(devices.data)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
